<template>
    <div
        class="sbs-radio group"
        :class="radioClasses">
        <label
            class="sbs-radio__item trs"
            :class="radioWrapperClasses"
            :for="radioId"
            @click="labelClickHandler">
            <!-- @slot Слот для вставки контента перед Радиобаттоном (дублирует props `prepend`) -->
            <slot name="prepend">
                {{ prepend }}
            </slot>

            <span class="sbs-radio__control-wrapper">
                <input
                    v-model="currentValue"
                    v-bind="inputAttrs"
                    ref="input"
                    :id="radioId"
                    class="sbs-radio__input visually-hidden"
                    :disabled="isDisabled"
                    :name="radioName"
                    :value="value"
                    type="radio"
                    @focus="inputFocusHandler"
                    @blur="inputBlurHandler">

                <!-- @slot Слот для кастомного элемента управления -->
                <slot
                    v-if="!radioHideControl"
                    name="control"
                    v-bind="{
                        checked: isChecked,
                        valid,
                    }">
                    <span
                        class="sbs-radio__control"
                        :class="radioControlClasses"></span>
                </slot>
            </span>

            <span
                v-if="isLabelVisible"
                class="sbs-radio__label">
                <!-- @slot Слот по-умолчанию для вставки контента после Радиобаттона (дублирует props `label`) -->
                <slot>{{ label }}</slot>
            </span>
        </label>

        <!-- @slot Слот для вывода ошибок (дублирует props `errorMessage`) -->
        <slot
            name="error"
            v-bind="{
                rules,
                valid,
                value: modelValue,
            }">
            <span
                v-if="invalid"
                class="sbs-radio__error-message">
                <SbsIcon
                    v-bind="errorIconData"
                    class="sbs-radio__error-icon" />
                {{ errorMessage || defaultErrorMessage }}
            </span>
        </slot>

        <!-- @slot Слот для нижнего подчеркивания -->
        <slot
            v-if="underlined"
            name="underline">
            <span
                class="sbs-radio__underline trs"
                :class="radioUnderlineClasses"></span>
        </slot>
    </div>
</template>

<script>
import themesMixin from '../../mixins/themes.js';
import validationMixin from '../../mixins/validation.js';
import { isArrayContainsValue } from '../../utils/validators.js';
import { AllColors } from '../../utils/colors.js';
import { getRandomId } from '../../utils/helpers.js';
import {
    AllSizes,
    RADIO_SIZES,
} from '../../utils/sizes.js';
import { AllIconGroups } from '../icon/utils/consts.js';

/**
 * Компонент-переключатель. В сочетании с компонентом SbsRadioGroup позволяет
 * сделать выбор одной из взаимоисключаемых альтернатив.
 */
export default {
    name: 'SbsRadio',
    mixins: [themesMixin, validationMixin],
    inject: {
        radioGroupComponent: { default: null },
    },
    emits: ['update:modelValue', 'valid', 'invalid'],
    data() {
        return {
            isFocused: false,
        };
    },
    computed: {
        isGroup() {
            return Boolean(this.radioGroupComponent);
        },
        radioGroupValue() {
            return this.radioGroupComponent?.modelValue;
        },
        radioGroupName() {
            return this.radioGroupComponent?.name;
        },
        radioGroupHideControl() {
            return this.radioGroupComponent?.hideControl;
        },
        radioGroupInline() {
            return this.radioGroupComponent?.inline;
        },
        radioGroupDisabled() {
            return this.radioGroupComponent?.disabled;
        },
        currentValue: {
            get() {
                return this.isGroup ? this.radioGroupValue : this.modelValue;
            },
            async set(value) {
                this.dirty = true;

                if (this.isGroup) {
                    if (this.radioGroupComponent) {
                        await this.radioGroupComponent.radioChangeHandler(value);
                    }
                } else {
                    /**
                     * Событие `update:modelValue` срабатывает при изменении значения.
                     * В качестве параметра - значение после изменения
                     */
                    this.$emit('update:modelValue', value);
                }

                if (!this.noValidate) {
                    await this.validate(value);
                }
            },
        },
        isChecked() {
            return this.isGroup
                ? this.value === this.radioGroupValue
                : this.value === this.modelValue;
        },
        isLabelVisible() {
            return Boolean(this.$slots.default || this.label);
        },
        isDisabled() {
            return this.disabled || this.radioGroupDisabled;
        },
        radioId() {
            return this.id || `sbs-radio-${getRandomId()}`;
        },
        radioName() {
            return this.name || this.radioGroupName;
        },
        radioHideControl() {
            return this.hideControl || this.radioGroupHideControl;
        },
        radioInline() {
            return this.inline || this.radioGroupInline;
        },
        radioClasses() {
            const classes = [];

            if (this.size) {
                classes.push(this.size);
            }

            if (this.selectedClass && this.isChecked) {
                classes.push(this.selectedClass);
            }

            return [
                this.$attrs.class,
                ...classes,
                {
                    'focused': this.isFocused,
                    'disabled': this.isDisabled,
                    'checked': this.isChecked,
                    'inline': this.radioInline,
                    'full-width': this.fullWidth,
                },
            ];
        },
        radioWrapperClasses() {
            const isArrayClasses = Array.isArray(this.labelClass);

            if (isArrayClasses) return [...this.themeClasses, ...this.labelClass];

            return [...this.themeClasses, this.labelClass];
        },
        radioControlClasses() {
            const classes = [];

            if (this.controlTheme) {
                classes.push(`bg-${this.controlTheme}`);
            }

            if (this.hoverControl) {
                classes.push(`group-hover-bg-${this.hoverControl}`);
            }

            return classes;
        },
        radioUnderlineClasses() {
            const classes = [];

            if (this.underlined) {
                if (this.underlineTheme) {
                    classes.push(`bg-${this.underlineTheme}`);
                }

                if (this.hoverUnderline && !this.isDisabled) {
                    classes.push(`group-hover-bg-${this.hoverUnderline}`);
                }
            }

            return classes;
        },
        errorIconData() {
            return {
                iconName: 'exclamation',
                iconGroup: AllIconGroups.SOLID,
                iconSize: AllSizes.SMALL,
                colorTheme: AllColors.WHITE,
                backgroundTheme: AllColors.GRADIENT_PINK,
            };
        },
        inputAttrs() {
            const { class: _, ...attrs } = this.$attrs;

            return attrs;
        },
    },
    methods: {
        inputFocusHandler() {
            this.isFocused = true;
        },
        inputBlurHandler() {
            this.isFocused = false;
        },
        labelClickHandler() {
            this.$refs.input.blur();
        },
    },
    inheritAttrs: false,
    props: {
        /**
         * Состояние радиобаттона (выбран или нет).
         * Принимает текущее выбранное значение (`value`)
         * @model
         */
        modelValue: {
            type: String,
            default: '',
        },
        /**
         * Тема (цветовые решения) для цвета фона элемента управления
         */
        controlTheme: {
            type: String,
            default: '',
        },
        /**
         * Перевод в неактивное состояние
         */
        disabled: {
            type: Boolean,
            default: false,
        },
        /**
         * Поле для вывода ошибок
         */
        errorMessage: {
            type: String,
            default: '',
        },
        /**
         * Определяет будет ли радиобаттон занимать всю доступную ширину
         */
        fullWidth: {
            type: Boolean,
            default: false,
        },
        /**
         * Скрывает элемент управления
         */
        hideControl: {
            type: Boolean,
            default: false,
        },
        /**
         * Тема (цветовые решения) для цвета фона при наведении
         */
        // eslint-disable-next-line vue/no-unused-properties
        hoverBackground: {
            type: String,
            default: AllColors.GRAY_ULTRA_LIGHT,
        },
        /**
         * Тема (цветовые решения) для цвета фона элемента управления при наведении
         */
        hoverControl: {
            type: String,
            default: '',
        },
        /**
         * Тема (цветовые решения) для цвета сепаратора при наведении
         */
        hoverUnderline: {
            type: String,
            default: AllColors.TRANSPARENT,
        },
        /**
         * Уникальный идентификатор
         */
        id: {
            type: String,
            default: '',
        },
        /**
         * Включает расположение элементов в строку
         */
        inline: {
            type: Boolean,
            default: false,
        },
        /**
         * Контент, расположенный после радиобаттона
         */
        label: {
            type: String,
            default: '',
        },
        /**
         * Класс для лэйбла
         */
        labelClass: {
            type: [Array, Object, String],
            default: '',
        },
        /**
         * Название радиобаттона, используется для объединения в общую группу
         */
        name: {
            type: String,
            default: '',
        },
        /**
         * Контент, расположенный перед радиобаттоном
         */
        prepend: {
            type: String,
            default: '',
        },
        /**
         * Класс для активного состояния (радиобаттон выбран)
         */
        selectedClass: {
            type: String,
            default: '',
        },
        /**
         * Размер радиобаттона.
         * Принимает значения: `medium`, `large`
         */
        size: {
            type: String,
            default: AllSizes.MEDIUM,
            validator: isArrayContainsValue(RADIO_SIZES),
        },
        /**
         * Включает отображение сепаратора
         */
        underlined: {
            type: Boolean,
            default: false,
        },
        /**
         * Тема (цветовые решения) для сепаратора
         */
        underlineTheme: {
            type: String,
            default: AllColors.GRAY_LIGHT,
        },
        /**
         * Значение радиобаттона
         */
        value: {
            type: String,
            default: '',
        },
    },
};
</script>

<style lang="scss">
.sbs-radio {
    position: relative;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    max-width: 100%;

    &.inline {
        display: inline-flex;
    }

    &.full-width {
        align-items: stretch;
        width: 100%;
    }

    &.disabled {
        cursor: not-allowed;
        user-select: none;

        & > * {
            pointer-events: none;
        }
    }

    &__item {
        display: flex;
        flex-grow: 1;
        align-items: center;
        padding: 5px 10px;
        word-wrap: break-word;
        word-break: break-word;
        border-radius: $rounded-10;
        cursor: pointer;

        &:not(.disabled):active {
            opacity: 0.6;
        }

        .sbs-radio.focused & {
            outline: 1px auto $current-color;
        }

        .sbs-radio.large & {
            padding: 20px 15px;
            border-radius: $rounded-15;
        }
    }

    &__label {
        display: flex;
        flex-direction: column;
        flex-grow: 1;
        user-select: none;

        @include typo-secondary-book;

        .sbs-radio.checked & {
            @include typo-secondary-demi;
        }

        .sbs-radio.disabled & {
            color: $gray;
        }
    }

    &__input {
        position: absolute;
        top: 50%;
        left: 50%;
    }

    &__control-wrapper {
        position: relative;
        display: flex;
        margin-right: 10px;
    }

    &__control {
        position: relative;
        display: block;
        flex-shrink: 0;
        width: 18px;
        height: 18px;
        background-color: $white;
        border: 1px solid $gray;
        border-radius: $rounded-circle;
        transition: $trs;
        content: "";

        .sbs-radio.checked & {
            background-color: $additional;
            border-color: rgba($black, 0.15);
        }

        .sbs-radio.disabled & {
            background-color: $gray-ultra-light;
        }

        .sbs-radio.large & {
            width: 30px;
            height: 30px;
        }

        .sbs-radio.large.disabled & {
            background-color: $white;
        }

        &::after {
            position: absolute;
            top: 50%;
            left: 50%;
            width: 8px;
            height: 8px;
            background-color: $black;
            border-radius: $rounded-circle;
            transform: translate(-50%, -50%) scale(0.4);
            opacity: 0;
            transition: $trs;
            content: "";

            .sbs-radio.checked & {
                transform: translate(-50%, -50%) scale(1);
                opacity: 1;
            }

            .sbs-radio.checked.disabled & {
                opacity: 0.5;
            }

            .sbs-radio.large & {
                width: 14px;
                height: 14px;
            }
        }
    }

    &__error-message {
        margin-top: 8px;
        color: $danger;

        @include typo-subsecondary-demi;
    }

    &__error-icon {
        width: 15px;
        height: 15px;
        margin-right: 5px;
        border-radius: $rounded-circle;
    }

    &__underline {
        align-self: stretch;
        height: 1px;
        margin-top: -1px;
        margin-right: 10px;
        margin-left: 10px;
        pointer-events: none;

        .sbs-radio.large & {
            margin-right: 15px;
            margin-left: 15px;
        }
    }
}
</style>
